import { rest } from "msw";

import { joinPaths } from "../utils";

const baseURL = import.meta.env.VITE_API_BASE_URL;

interface MockSalesRepresentative {
  id: string;
  name: string;
  email: string;
  phone: string;
  document: string;
  commissionRate: number; // fractional (0.10 = 10%)
  isActive: boolean;
  notes: string | null;
  createdDate: string;
  updatedDate: string;
}

const salesRepresentatives: MockSalesRepresentative[] = [
  {
    id: "1",
    name: "John Doe",
    email: "john.doe@example.com",
    phone: "(11) 98765-4321",
    document: "123.456.789-00",
    commissionRate: 0.055,
    isActive: true,
    notes: "Top performer in downtown region",
    createdDate: "2024-01-15T10:00:00Z",
    updatedDate: "2024-04-20T10:00:00Z",
  },
  {
    id: "2",
    name: "Jane Smith",
    email: "jane.smith@example.com",
    phone: "(11) 98765-4322",
    document: "987.654.321-00",
    commissionRate: 0.07,
    isActive: true,
    notes: null,
    createdDate: "2024-02-20T10:00:00Z",
    updatedDate: "2024-04-20T10:00:00Z",
  },
  {
    id: "3",
    name: "Bob Brown",
    email: "bob.brown@example.com",
    phone: "(11) 98765-4323",
    document: "111.222.333-44",
    commissionRate: 0.04,
    isActive: false,
    notes: "On leave until June 2024",
    createdDate: "2023-12-10T10:00:00Z",
    updatedDate: "2024-03-15T10:00:00Z",
  },
  {
    id: "4",
    name: "Alice Green",
    email: "alice.green@example.com",
    phone: "(11) 98765-4324",
    document: "555.666.777-88",
    commissionRate: 0.06,
    isActive: true,
    notes: "Responsible for north region",
    createdDate: "2024-03-05T10:00:00Z",
    updatedDate: "2024-04-18T10:00:00Z",
  },
];

const commissionReport = {
  totalGrossAll: 125000.75,
  totalCommissionAll: 8125.45,
  salesRepresentatives: [
    {
      id: "1",
      name: "John Doe",
      commissionRate: 0.055,
      totalGross: 85000.5,
      totalCommission: 4675.28,
      restaurants: [
        {
          restaurantId: "r1",
          restaurantName: "Burger King - Paulista",
          grossTotal: 25000.0,
          commissionAmount: 1375.0,
        },
        {
          restaurantId: "r2",
          restaurantName: "McDonald's - Augusta",
          grossTotal: 18000.0,
          commissionAmount: 990.0,
        },
        {
          restaurantId: "r3",
          restaurantName: "Subway - Consolacao",
          grossTotal: 15000.0,
          commissionAmount: 825.0,
        },
        {
          restaurantId: "r4",
          restaurantName: "Starbucks - Faria Lima",
          grossTotal: 17000.5,
          commissionAmount: 935.28,
        },
        {
          restaurantId: "r5",
          restaurantName: "Outback - Morumbi",
          grossTotal: 10000.0,
          commissionAmount: 550.0,
        },
      ],
    },
    {
      id: "2",
      name: "Jane Smith",
      commissionRate: 0.07,
      totalGross: 40000.25,
      totalCommission: 2450.17,
      restaurants: [
        {
          restaurantId: "r6",
          restaurantName: "Pizza Hut - Ibirapuera",
          grossTotal: 15000.25,
          commissionAmount: 1051.77,
        },
        {
          restaurantId: "r7",
          restaurantName: "KFC - Tatuape",
          grossTotal: 12000.0,
          commissionAmount: 840.0,
        },
        {
          restaurantId: "r8",
          restaurantName: "Giraffas - Santo Amaro",
          grossTotal: 13000.0,
          commissionAmount: 558.4,
        },
      ],
    },
  ],
};

function paginate<T>(
  data: T[],
  page: number,
  limit: number
): { data: T[]; total: number; currentPage: number; pageCount: number } {
  const total = data.length;
  const pageCount = Math.max(1, Math.ceil(total / limit));
  const start = (page - 1) * limit;

  return {
    data: data.slice(start, start + limit),
    total,
    currentPage: page,
    pageCount,
  };
}

export const salesRepresentativeMocks = [
  rest.get(joinPaths(baseURL, "admin/sales_representative"), (req, res, ctx) => {
    const url = new URL(req.url.toString());
    const nameFilter = url.searchParams.get("name") || "";
    const emailFilter = url.searchParams.get("email") || "";
    const isActiveParam = url.searchParams.get("isActive");
    const page = parseInt(url.searchParams.get("page") || "1", 10);
    const limit = parseInt(url.searchParams.get("limit") || "10", 10);

    let data = [...salesRepresentatives];

    if (nameFilter) {
      const term = nameFilter.toLowerCase();
      data = data.filter((d) => d.name.toLowerCase().includes(term));
    }
    if (emailFilter) {
      const term = emailFilter.toLowerCase();
      data = data.filter((d) => d.email.toLowerCase().includes(term));
    }
    if (isActiveParam !== null) {
      const bool = isActiveParam === "true";
      data = data.filter((d) => d.isActive === bool);
    }

    return res(ctx.status(200), ctx.json(paginate(data, page, limit)), ctx.delay(300));
  }),

  rest.get(
    joinPaths(baseURL, "admin/sales_representative/commissions/report"),
    (_req, res, ctx) => {
      return res(ctx.status(200), ctx.json(commissionReport), ctx.delay(500));
    }
  ),

  rest.get(joinPaths(baseURL, "admin/sales_representative/:id"), (req, res, ctx) => {
    const { id } = req.params;
    const item = salesRepresentatives.find((s) => s.id === id);

    if (!item) return res(ctx.status(404), ctx.json({ message: "NOT_FOUND" }));

    return res(ctx.status(200), ctx.json(item), ctx.delay(300));
  }),

  rest.post(joinPaths(baseURL, "admin/sales_representative"), (req, res, ctx) => {
    const body = req.body as Partial<MockSalesRepresentative>;
    const newItem: MockSalesRepresentative = {
      id: String(salesRepresentatives.length + 1),
      name: body.name ?? "",
      email: body.email ?? "",
      phone: body.phone ?? "",
      document: body.document ?? "",
      commissionRate: body.commissionRate ?? 0,
      isActive: body.isActive ?? true,
      notes: body.notes ?? null,
      createdDate: new Date().toISOString(),
      updatedDate: new Date().toISOString(),
    };
    salesRepresentatives.push(newItem);

    return res(ctx.status(201), ctx.json(newItem), ctx.delay(300));
  }),

  rest.patch(joinPaths(baseURL, "admin/sales_representative/:id"), (req, res, ctx) => {
    const { id } = req.params;
    const body = req.body as Partial<MockSalesRepresentative>;
    const index = salesRepresentatives.findIndex((s) => s.id === id);
    if (index === -1) return res(ctx.status(404), ctx.json({ message: "NOT_FOUND" }));

    salesRepresentatives[index] = {
      ...salesRepresentatives[index],
      ...body,
      updatedDate: new Date().toISOString(),
    };

    return res(ctx.status(200), ctx.json(salesRepresentatives[index]), ctx.delay(300));
  }),

  rest.delete(joinPaths(baseURL, "admin/sales_representative/:id"), (req, res, ctx) => {
    const { id } = req.params;
    const index = salesRepresentatives.findIndex((s) => s.id === id);
    if (index === -1) return res(ctx.status(404), ctx.json({ message: "NOT_FOUND" }));

    salesRepresentatives.splice(index, 1);

    return res(ctx.status(200), ctx.delay(300));
  }),
];
